'use client';

import { useTranslations } from 'next-intl';

import { BaseModal } from './BaseModal';
import { ExecutionEnvironmentView } from './ExecutionEnvironmentView';
import type { ExecutionEnvironmentSection } from '../lib/executionEnvironmentModal';

type ExecutionEnvironmentModalProps = {
  isOpen: boolean;
  onClose: () => void;
  /** 진입 시 열어 둘 섹션. 러너 CTA는 `runners`, 기존 모델 진입점은 `models`로 연다. */
  initialSection?: ExecutionEnvironmentSection;
};

/**
 * 계정 범위 `내 실행 환경` 모달.
 *
 * 러너·개인 모델은 프로젝트가 아니라 사용자 계정 자산이다. 프로젝트 사이드바 셸 안의 페이지로 띄우면
 * 프로젝트에 종속된 설정으로 읽히므로, 개인 설정 화면이 생기기 전까지는 어느 화면에서든 전역 모달로 연다.
 */
export const ExecutionEnvironmentModal = ({
  isOpen,
  onClose,
  initialSection = 'runners',
}: ExecutionEnvironmentModalProps) => {
  const t = useTranslations('executionEnvironment');

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      variant="bottomSheet"
      size="2xl"
      height="h-[min(42rem,calc(100dvh-4rem))]"
      className="flex min-h-[24rem] flex-col overflow-hidden"
    >
      <div className="mb-4 flex shrink-0 items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-base font-semibold">{t('title')}</h3>
          <p className="mt-1 text-xs leading-relaxed text-[var(--color-fg-muted)]">{t('description')}</p>
        </div>
        <button type="button" aria-label="Close" className="btn btn-ghost btn-circle btn-sm shrink-0" onClick={onClose}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <ExecutionEnvironmentView initialSection={initialSection} />
    </BaseModal>
  );
};
